/**
 * Bandeau « les disponibilités n'ont pas pu être chargées ».
 *
 * Raison d'être : jusqu'ici, un flux de disponibilité en échec (coupure réseau,
 * 502 du proxy, réponse HTML au lieu du JSON) se traduisait par un tableau vide,
 * indiscernable de « aucune réservation ». Le calendrier n'affichait aucun jour
 * pris, les pickers ne barraient rien, le contrôle de chevauchement ne trouvait
 * jamais de conflit — et l'usager réservait un samedi déjà loué sans qu'un seul
 * mot ne l'en avertisse. `apiClient.js` distingue désormais « rien » de « je ne
 * sais pas » et émet `gemengapp:disponibilites-incompletes` ; ce module en fait
 * quelque chose de visible.
 *
 * Choix délibéré : on AVERTIT, on ne BLOQUE pas. Un hoquet réseau ne doit pas
 * condamner un formulaire à moitié rempli, et GMS revalide de toute façon la
 * demande à l'enregistrement. Ce que l'usager doit savoir, c'est que l'écran
 * qu'il regarde peut être incomplet, et qu'un simple rechargement le corrige.
 */

import { disponibilitesIncertaines } from './apiClient.js';

const ID = 'availabilityNotice';

/**
 * Libellés, rendus traduits par le gabarit quand il les fournit.
 *
 * Même mécanique que `#materialStockLabels` et `#bookingFormLabels` : le script
 * n'a pas accès à `_()`, et le site est servi en français et en luxembourgeois.
 */
const DEFAUTS = {
    titre: "Les disponibilités n'ont pas pu être chargées.",
    texte:
        'Le calendrier peut donc afficher comme libres des dates déjà réservées. ' +
        'Rechargez la page avant de choisir vos dates ; en cas de doute, la commune ' +
        'vérifiera la disponibilité à réception de votre demande.',
    action: 'Recharger la page',
};

function libelles() {
    const bloc = document.getElementById('availabilityNoticeLabels');
    if (!bloc) return DEFAUTS;
    try {
        return { ...DEFAUTS, ...(JSON.parse(bloc.textContent) || {}) };
    } catch (e) {
        return DEFAUTS;
    }
}

/**
 * Où poser le bandeau : en tête du formulaire s'il y en a un, sinon au-dessus du
 * calendrier de la fiche salle. Les deux écrans consomment le même flux.
 */
function pointDAncrage() {
    return (
        document.querySelector('.form-bookingapp .row') ||
        document.querySelector('.calendar-booking')?.parentNode ||
        null
    );
}

function afficher() {
    if (document.getElementById(ID)) return; // un seul bandeau par page

    const hote = pointDAncrage();
    if (!hote) return;

    const l = libelles();

    const colonne = document.createElement('div');
    colonne.className = 'col-12 col-lg-8 offset-lg-2 col-xl-6 offset-xl-3';

    const bandeau = document.createElement('div');
    bandeau.id = ID;
    bandeau.className = 'alert alert-warning availability-notice mb-30';
    bandeau.setAttribute('role', 'alert');

    const titre = document.createElement('p');
    titre.className = 'availability-notice__title mb-5';
    const icone = document.createElement('i');
    icone.className = 'fa-solid fa-triangle-exclamation me-2';
    icone.setAttribute('aria-hidden', 'true');
    const fort = document.createElement('strong');
    fort.textContent = l.titre;
    titre.append(icone, fort);

    const texte = document.createElement('p');
    texte.className = 'mb-10 small';
    texte.textContent = l.texte;

    const action = document.createElement('button');
    action.type = 'button';
    action.className = 'btn-custom ff-main fw-400 availability-notice__retry';
    action.textContent = l.action;
    action.addEventListener('click', () => window.location.reload());

    bandeau.append(titre, texte, action);
    colonne.appendChild(bandeau);
    hote.prepend(colonne);
}

export function initAvailabilityNotice() {
    window.addEventListener('gemengapp:disponibilites-incompletes', afficher);

    // Filet : `flatpickr-init-app.js` est importé avant ce module et lance son
    // chargement dès `DOMContentLoaded`. L'appel étant asynchrone, l'événement ne
    // peut pas partir avant cet abonnement — mais si l'ordre d'initialisation
    // changeait un jour, l'avertissement disparaîtrait sans bruit. On relit donc
    // aussi l'état déjà connu.
    if (disponibilitesIncertaines()) afficher();
}
